import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, LogOut, RefreshCw, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "Accepted", "Declined"];

const STATUS_COLORS = {
  New: "text-[#CCFF00] border-[#CCFF00]/40",
  Contacted: "text-sky-400 border-sky-400/40",
  Accepted: "text-emerald-400 border-emerald-400/40",
  Declined: "text-zinc-500 border-zinc-700",
};

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem("admin_token", data.token);
      onLogin(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center px-4" data-testid="admin-login-screen">
      <form onSubmit={submit} className="w-full max-w-sm border border-zinc-800 bg-[#141416] p-8 sm:p-10">
        <p className="font-display text-3xl text-white tracking-wide mb-1">
          COACH LOGIN<span className="text-[#CCFF00]">.</span>
        </p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono2 mb-8">
          Applications Dashboard
        </p>
        <label className="field-label" htmlFor="admin-email">Email</label>
        <input id="admin-email" data-testid="admin-email-input" type="email" required
          className="field-input mb-5" value={email} onChange={(e) => setEmail(e.target.value)} />
        <label className="field-label" htmlFor="admin-password">Password</label>
        <input id="admin-password" data-testid="admin-password-input" type="password" required
          className="field-input mb-6" value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && (
          <p className="text-xs text-red-400 font-mono2 mb-4" data-testid="admin-login-error">{error}</p>
        )}
        <button type="submit" disabled={loading} data-testid="admin-login-button"
          className="w-full bg-[#CCFF00] text-[#0B0B0C] font-bold uppercase tracking-[0.15em] text-sm py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60">
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token"));
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const fetchApps = async (t = token) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/applications`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setApps(data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        setToken(null);
      } else {
        setError("Failed to load applications");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchApps(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/applications/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch {
      setError("Failed to update status");
    }
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return apps;
    return apps.filter((a) =>
      [a.full_name, a.email, a.position, a.current_level, a.status]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [apps, query]);

  const exportCsv = () => {
    const cols = ["full_name", "email", "phone", "age", "position", "current_level", "start_timeline", "decision_maker", "current_program", "ready_to_invest", "biggest_goal", "current_schedule", "injury_history", "whats_holding_back", "why_interested", "status", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...filtered.map((a) => cols.map((c) => esc(a[c])).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "applications.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-white" data-testid="admin-dashboard">
      <header className="border-b border-zinc-800 sticky top-0 bg-[#0B0B0C]/90 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <p className="font-display text-2xl tracking-wide">
            APPLICATIONS<span className="text-[#CCFF00]">.</span>
          </p>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => fetchApps()} data-testid="admin-refresh-button"
              className="p-2.5 border border-zinc-800 hover:border-[#CCFF00]/50 transition-colors" title="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={exportCsv} data-testid="admin-export-csv-button"
              className="p-2.5 border border-zinc-800 hover:border-[#CCFF00]/50 transition-colors" title="Export CSV">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
              className="p-2.5 border border-zinc-800 hover:border-red-400/60 hover:text-red-400 transition-colors" title="Log out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="admin-search-input"
              className="field-input pl-11"
              placeholder="Search name, position, level, status..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono2 shrink-0" data-testid="admin-count">
            {filtered.length} / {apps.length} applications
          </p>
        </div>

        {error && <p className="text-sm text-red-400 font-mono2 mb-6" data-testid="admin-error">{error}</p>}

        {!loading && filtered.length === 0 && (
          <div className="border border-zinc-800 bg-[#141416] p-14 text-center" data-testid="admin-empty-state">
            <p className="font-display text-3xl text-zinc-500 uppercase tracking-wide">No applications yet</p>
            <p className="text-xs text-zinc-600 font-mono2 mt-2 uppercase tracking-[0.2em]">
              New submissions will appear here
            </p>
          </div>
        )}

        <div className="grid gap-4">
          {filtered.map((a) => (
            <article key={a.id} className="border border-zinc-800 bg-[#141416] p-6 sm:p-8" data-testid={`application-card-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                  <h3 className="font-display uppercase text-2xl sm:text-3xl text-white leading-none">
                    {a.full_name}
                  </h3>
                  <p className="text-xs text-zinc-500 font-mono2 mt-2">
                    {a.email}{a.phone ? ` · ${a.phone}` : ""} · Age {a.age} · {a.position} · {a.current_level}
                  </p>
                </div>
                <select
                  value={a.status}
                  onChange={(e) => updateStatus(a.id, e.target.value)}
                  data-testid={`application-status-select-${a.id}`}
                  className={`bg-[#0B0B0C] border px-3 py-2 text-xs uppercase tracking-[0.15em] font-mono2 outline-none cursor-pointer ${STATUS_COLORS[a.status] || STATUS_COLORS.New}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                {[
                  ["Biggest Goal", a.biggest_goal],
                  ["Current Schedule", a.current_schedule],
                  ["Holding Them Back", a.whats_holding_back],
                  ["Why 1-on-1", a.why_interested],
                  ["Current Program", a.current_program],
                  ["Start Timeline", a.start_timeline],
                  ["Decision Maker", a.decision_maker],
                  ["Ready To Invest", a.ready_to_invest],
                  ...(a.injury_history ? [["Injury History", a.injury_history]] : []),
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono2 mb-1">{label}</dt>
                    <dd className="text-zinc-300 leading-relaxed">{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-[10px] text-zinc-600 font-mono2 mt-5 uppercase tracking-[0.15em]">
                Submitted {new Date(a.created_at).toLocaleString()}
              </p>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
